import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { FeatureGate } from './components/plan/FeatureGate';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Sidebar } from '@cloudflare/kumo';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Navigation } from './components/Navigation';
import { TopHeader } from './components/TopHeader';
import { ContactsPage } from './pages/ContactsPage';
import { WhatsAppConnectionsPage } from './pages/WhatsAppConnectionsPage';
import { InteractiveCampaignPage } from './pages/InteractiveCampaignPage';
import { FlowBuilderPage } from './pages/FlowBuilderPage';
import { AutomacoesPage } from './pages/AutomacoesPage';
import { AuditoriaPage } from './pages/AuditoriaPage';
import { LgpdPage } from './pages/LgpdPage';
import { CaptureFormPage } from './pages/CaptureFormPage';
import { RespostasAutomaticasPage } from './pages/RespostasAutomaticasPage';
import { SettingsPage } from './pages/SettingsPage';
import { AppFooter } from './components/AppFooter';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { SuportePage } from './pages/SuportePage';
import { UsersPage } from './pages/UsersPage';
import { LoginPage } from './pages/LoginPage';
import { InvitePage } from './pages/InvitePage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AutenticacaoPage } from './pages/perfil/AutenticacaoPage';
import { AplicativosPage } from './pages/perfil/AplicativosPage';
import { SessoesPage } from './pages/perfil/SessoesPage';
import { TokensPage } from './pages/perfil/TokensPage';
import { PerfilPage } from './pages/PerfilPage';
import { FerramentasPage } from './pages/ferramentas/FerramentasPage';
import { CobrancasPage } from './pages/ferramentas/CobrancasPage';
import { InstagramPage } from './pages/ferramentas/InstagramPage';
import { CuponsPage } from './pages/ferramentas/CuponsPage';
import { ImagensDinamicasPage } from './pages/ferramentas/ImagensDinamicasPage';
import { LeadAdsPage } from './pages/ferramentas/LeadAdsPage';
import { SuperAdminManagerPage } from './pages/SuperAdminManagerPage';
import { FaturamentoPage } from './pages/FaturamentoPage';
import { NovaCampanhaInterativaPage } from './pages/NovaCampanhaInterativaPage';
import { CampanhaInterativaRelatorioPage } from './pages/CampanhaInterativaRelatorioPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { IntegracoesPage } from './pages/IntegracoesPage';
import { CaptacaoPage } from './pages/CaptacaoPage';
import { AskAiPage } from './pages/AskAiPage';
import { SegmentsPage } from './pages/SegmentsPage';
import { CustomFieldsPage } from './pages/CustomFieldsPage';
import { SequencesPage } from './pages/SequencesPage';
import { AiSkillsGalleryPage } from './pages/ia/AiSkillsGalleryPage';
import { AiSkillsPage } from './pages/ia/AiSkillsPage';
import { AiAgentsPage } from './pages/ia/AiAgentsPage';
import { ObservabilidadePage } from './pages/ia/ObservabilidadePage';
import { AiKnowledgePage } from './pages/ia/AiKnowledgePage';
import { AiWidgetPage } from './pages/ia/AiWidgetPage';
import { AiToolsPage } from './pages/ia/AiToolsPage';
import { AiToolsCatalogPage } from './pages/ia/AiToolsCatalogPage';
import { AiToolDetailPage } from './pages/ia/AiToolDetailPage';
import { AiToolInstallPage } from './pages/ia/AiToolInstallPage';
import { SequenceEditorPage } from './pages/SequenceEditorPage';
import { OrganizacaoPage } from './pages/OrganizacaoPage';
import { SignupPage } from './pages/SignupPage';
import { InboxPage } from './pages/InboxPage';
import { FunilPage } from './pages/FunilPage';
import MetricasPage from './pages/MetricasPage';
import PaineisPage from './pages/PaineisPage';
import { PlanProvider } from './contexts/PlanContext';
import { PlanBanner, UpgradeModal } from './components/PlanBanner';
import { ToastFlashbar } from './components/ui/ToastFlashbar';
import { useAiStatus } from './components/AiAssistant';
import { CommandPalette } from './components/search/CommandPalette';
import { jevEvents } from './components/jev/jevEvents';
import './styles/globals.css';
import './styles/theme.css';
import { PageSkeleton } from './components/ui/Skeleton';

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  const { pathname } = useLocation();
  const aiStatus = useAiStatus();
  // Mostra "Perguntar à Lumi" na busca global quando a Lumi está habilitada
  // (mesma regra que o TopHeader usava para o JevPanel)
  const useOldAssistant = (() => { try { return localStorage.getItem('ingrave:assistente') === 'antigo'; } catch { return false; } })();
  const jevEnabled = !useOldAssistant && !!aiStatus?.available && aiStatus?.jev !== false;

  // Menu lateral recolhido: o estado vive aqui porque o AppLayout (que controla
  // a largura da navegação) e o SideNavigation precisam concordar.
  // No celular o menu é uma gaveta que nasce fechada; o recolhido do desktop não pode abri-la
  const narrowQuery = '(max-width: 767px)';
  const [isNarrow, setIsNarrow] = useState<boolean>(() => typeof window !== 'undefined' && window.matchMedia(narrowQuery).matches);
  const [navMobileOpen, setNavMobileOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(narrowQuery);
    const on = () => { setIsNarrow(mq.matches); if (!mq.matches) setNavMobileOpen(false); };
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('nav_collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('nav_collapsed', navCollapsed ? '1' : '0'); } catch { /* sem storage */ }
  }, [navCollapsed]);

  // Altura do cabeçalho fixo em --crm-header-h: telas que ocupam exatamente a
  // área visível (editor de fluxo, caixa de entrada) descontam dela. Muda no
  // celular e quando o cabeçalho quebra linha, por isso é medida.
  useEffect(() => {
    const header = document.getElementById('crm-top-header');
    if (!header || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--crm-header-h', `${header.offsetHeight}px`);
    });
    ro.observe(header);
    return () => ro.disconnect();
  }, [isAuthenticated, isLoading]);

  // Painel da Lumi desliza o shell lateralmente (estilo Cloudflare Ask AI).
  // O JevPanel emite 'open' / 'close' no jevEvents; aqui alternamos a classe
  // em <div className="app-shell"> que o CSS anima com transform.
  const shellRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const apply = (on: boolean) => {
      shellRef.current?.classList.toggle('app-shell--jev-open', on);
    };
    const onOpen = () => apply(true);
    const onClose = () => apply(false);
    jevEvents.addEventListener('open', onOpen);
    jevEvents.addEventListener('close', onClose);
    return () => {
      jevEvents.removeEventListener('open', onOpen);
      jevEvents.removeEventListener('close', onClose);
    };
  }, []);

  // Mostrar loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <div className="min-h-screen"><PageSkeleton label="Carregando" /></div>
    );
  }

  // Aceite de convite e formulário público de captação: tela própria, com ou sem sessão aberta
  if (pathname.startsWith('/convite/') || pathname.startsWith('/redefinir-senha/') || pathname.startsWith('/f/')) {
    return (
      <Routes>
        <Route path="/f/:id" element={<CaptureFormPage />} />
        <Route path="/convite/:token" element={<InvitePage />} />
        <Route path="/redefinir-senha/:token" element={<ResetPasswordPage />} />
      </Routes>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/cadastro" element={<SignupPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <PlanProvider>
    <CommandPalette jevEnabled={jevEnabled} />
    <Sidebar.Provider
      open={isNarrow ? navMobileOpen : !navCollapsed}
      onOpenChange={(open) => (isNarrow ? setNavMobileOpen(open) : setNavCollapsed(!open))}
      collapsible="icon"
      mobileBreakpoint={768}
    >
    <div ref={shellRef} className="app-shell">
      <Navigation />
      <div className="flex min-w-0 flex-1 flex-col">
        <ImpersonationBanner />
        <div id="crm-top-header" className="crm-top-header">
          <TopHeader />
        </div>
        <ToastFlashbar />
        <main className="crm-app-content min-w-0 flex-1">
          <div>
            <PlanBanner />
            <UpgradeModal />
            <TenantGate>
            <Routes>
          <Route path="/login" element={<Navigate to="/dashboard" replace />} />
          {/* Já tem conta: o "Teste grátis" do site leva para a escolha do plano */}
          <Route path="/cadastro" element={<Navigate to="/faturamento" replace />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AskAiPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contatos"
            element={
              <ProtectedRoute>
                <ContactsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/whatsapp"
            element={
              <ProtectedRoute>
                <WhatsAppConnectionsPage />
              </ProtectedRoute>
            }
          />
          {/* Disparo em massa foi removido: links antigos vão para as campanhas interativas */}
          <Route path="/campanhas" element={<Navigate to="/campanhas/interativa" replace />} />
          <Route path="/campanhas/nova" element={<Navigate to="/campanhas/interativa" replace />} />
          <Route
            path="/campanhas/interativa"
            element={
              <ProtectedRoute><FeatureGate feature="interactive">
                <InteractiveCampaignPage />
              </FeatureGate></ProtectedRoute>
            }
          />
          <Route
            path="/campanhas/interativa/nova"
            element={
              <ProtectedRoute><FeatureGate feature="interactive">
                <NovaCampanhaInterativaPage />
              </FeatureGate></ProtectedRoute>
            }
          />
          <Route
            path="/campanhas/interativa/:id/relatorio"
            element={
              <ProtectedRoute><FeatureGate feature="interactive">
                <CampanhaInterativaRelatorioPage />
              </FeatureGate></ProtectedRoute>
            }
          />
          <Route
            path="/campanhas/interativa/:id"
            element={
              <ProtectedRoute><FeatureGate feature="interactive">
                <FlowBuilderPage />
              </FeatureGate></ProtectedRoute>
            }
          />
          <Route
            path="/configuracoes"
            element={
              <ProtectedRoute permission="integrations:manage">
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute superAdminOnly={true}>
                <SuperAdminManagerPage />
              </ProtectedRoute>
            }
          />
          <Route path="/superadmin/dashboard" element={<Navigate to="/super-admin?aba=visao-geral" replace />} />
          <Route path="/superadmin/tenants" element={<Navigate to="/super-admin?aba=empresas" replace />} />
          <Route
            path="/usuarios"
            element={
              <ProtectedRoute permission="members:read">
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route path="/planos" element={<Navigate to="/super-admin?aba=planos" replace />} />
          <Route
            path="/faturamento"
            element={
              <ProtectedRoute permission="billing:read">
                <FaturamentoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projetos"
            element={
              <ProtectedRoute>
                <ProjectsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizacao"
            element={
              <ProtectedRoute>
                <OrganizacaoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ferramentas"
            element={
              <ProtectedRoute>
                <FerramentasPage />
              </ProtectedRoute>
            }
          />
          <Route path="/integracoes" element={<ProtectedRoute><IntegracoesPage /></ProtectedRoute>} />
          <Route path="/captacao" element={<ProtectedRoute><CaptacaoPage /></ProtectedRoute>} />
          <Route path="/ferramentas/cobrancas" element={<ProtectedRoute><FeatureGate feature="pix"><CobrancasPage /></FeatureGate></ProtectedRoute>} />
          {/* A API oficial agora é a própria tela de Conexões */}
          <Route path="/ferramentas/api-oficial" element={<Navigate to="/whatsapp" replace />} />
          <Route path="/ferramentas/instagram" element={<ProtectedRoute><FeatureGate feature="instagram"><InstagramPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/ferramentas/cupons" element={<ProtectedRoute><CuponsPage /></ProtectedRoute>} />
          <Route path="/ferramentas/imagens" element={<ProtectedRoute><ImagensDinamicasPage /></ProtectedRoute>} />
          <Route path="/ferramentas/lead-ads" element={<ProtectedRoute><FeatureGate feature="lead_ads"><LeadAdsPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/perfil" element={<ProtectedRoute><PerfilPage /></ProtectedRoute>} />
          <Route path="/perfil/autenticacao" element={<ProtectedRoute><AutenticacaoPage /></ProtectedRoute>} />
          <Route path="/perfil/aplicativos" element={<ProtectedRoute><AplicativosPage /></ProtectedRoute>} />
          <Route path="/perfil/sessoes" element={<ProtectedRoute><SessoesPage /></ProtectedRoute>} />
          <Route path="/perfil/tokens" element={<ProtectedRoute><TokensPage /></ProtectedRoute>} />
          <Route path="/conta" element={<Navigate to="/perfil/autenticacao" replace />} />
          <Route
            path="/respostas-automaticas"
            element={
              <ProtectedRoute>
                <RespostasAutomaticasPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auditoria"
            element={
              <ProtectedRoute><FeatureGate feature="audit">
                <AuditoriaPage />
              </FeatureGate></ProtectedRoute>
            }
          />
          <Route
            path="/lgpd"
            element={
              <ProtectedRoute>
                <LgpdPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/automacoes"
            element={
              <ProtectedRoute>
                <AutomacoesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/segmentos"
            element={
              <ProtectedRoute>
                <SegmentsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/sequencias" element={<ProtectedRoute><FeatureGate feature="sequences"><SequencesPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/ia/skills/galeria" element={<ProtectedRoute><FeatureGate feature="ai_agents"><AiSkillsGalleryPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/ia/skills" element={<ProtectedRoute><FeatureGate feature="ai_agents"><AiSkillsPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/ia/observabilidade" element={<ProtectedRoute><FeatureGate feature="ai_agents"><ObservabilidadePage /></FeatureGate></ProtectedRoute>} />
          <Route path="/ia/agentes" element={<ProtectedRoute><FeatureGate feature="ai_agents"><AiAgentsPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/ia/base" element={<ProtectedRoute><FeatureGate feature="knowledge_base"><AiKnowledgePage /></FeatureGate></ProtectedRoute>} />
          <Route path="/ia/ferramentas" element={<ProtectedRoute><FeatureGate feature="ai_agents"><AiToolsCatalogPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/ia/ferramentas/minhas" element={<ProtectedRoute><FeatureGate feature="ai_agents"><AiToolsPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/ia/ferramentas/:slug" element={<ProtectedRoute><FeatureGate feature="ai_agents"><AiToolDetailPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/ia/ferramentas/:slug/instalar" element={<ProtectedRoute><FeatureGate feature="ai_agents"><AiToolInstallPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/ia/widget" element={<ProtectedRoute><FeatureGate feature="ai_widget"><AiWidgetPage /></FeatureGate></ProtectedRoute>} />
          <Route path="/sequencias/:id" element={<ProtectedRoute><FeatureGate feature="sequences"><SequenceEditorPage /></FeatureGate></ProtectedRoute>} />
          <Route
            path="/inbox"
            element={
              <ProtectedRoute permission="inbox:read">
                <FeatureGate feature="inbox">
                  <InboxPage />
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/campos"
            element={
              <ProtectedRoute>
                <CustomFieldsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/funil"
            element={
              <ProtectedRoute permission="funil:read">
                <FunilPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/metricas"
            element={
              <ProtectedRoute permission="data:read">
                <FeatureGate feature="advanced_reports">
                  <MetricasPage />
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          <Route path="/suporte" element={<ProtectedRoute><SuportePage /></ProtectedRoute>} />
          <Route path="/suporte/chamados" element={<ProtectedRoute><SuportePage view="casos" /></ProtectedRoute>} />
          <Route path="/suporte/novo" element={<ProtectedRoute><SuportePage view="novo" /></ProtectedRoute>} />
          <Route
            path="/paineis"
            element={
              <ProtectedRoute permission="data:read">
                <PaineisPage />
              </ProtectedRoute>
            }
          />
          {/* Endereço desconhecido dentro do app: volta ao início, em vez de uma área vazia */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </TenantGate>
          </div>
        </main>
        <AppFooter />
      </div>
    </div>
    </Sidebar.Provider>
    </PlanProvider>
  );
}

// As páginas só renderizam depois que o tenant (empresa) foi resolvido: se
// renderizassem antes, as primeiras requisições saíam sem X-Tenant-Id.
function TenantGate({ children }: { children: ReactNode }) {
  const { loading, selectedTenantId, selectedProjectId, mfaSetupRequired } = useTenant();
  const { pathname } = useLocation();
  if (loading) {
    return (
      <PageSkeleton label="Carregando empresa" />
    );
  }
  // Organização exige 2FA: até configurar, só Minha conta
  if (!loading && mfaSetupRequired && !pathname.startsWith('/perfil')) {
    return <Navigate to="/perfil/autenticacao" replace />;
  }
  // Trocar de projeto remonta as páginas, que buscam os dados do novo projeto
  return <Fragment key={`${selectedTenantId}:${selectedProjectId}`}>{children}</Fragment>;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <TenantProvider>
          <AppContent />
        </TenantProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;